/**
 * Annotation drawing tool (`simpleAnnotation`).
 *
 * Same geometry as klinecharts' built-in template (a dashed stem rising from
 * the anchor, a small arrowhead, and a label above it), re-registered under the
 * SAME name with interactive figures. The built-in marks every figure
 * `ignoreEvent: true` and draws no default point figure, so it has zero hit
 * area: nothing can select, drag or double-click it. Here the stem, head and
 * label take events and the anchor handle exists, so the overlay callbacks fire.
 *
 * Label contract: `overlay.extendData` as a plain string (saved + AI drawings),
 * or a function returning one (as upstream). Anything else renders no text.
 */
import { registerOverlay } from 'klinecharts'
import { isNum, lineFigure, type Coord, type OverlayFigure } from './overlayUtils'
import { DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

export interface AnnotationStyle {
  color?: string
  size?: number
}

/** Gap between the anchor and the stem start, px. */
const STEM_GAP = 6
/** Stem length, px. */
const STEM_LEN = 50
/** Arrowhead height / half-width, px. */
const HEAD_H = 5
const HEAD_HALF_W = 4

const finite = (p?: Coord) => !!p && isNum(p.x) && isNum(p.y)

/** Resolve the label: string as-is, function called with the overlay, else ''. */
export function resolveAnnotationText(overlay: unknown): string {
  const data = (overlay as { extendData?: unknown } | undefined)?.extendData
  const value = typeof data === 'function' ? (data as (o: unknown) => unknown)(overlay) : data
  return typeof value === 'string' ? value : ''
}

export function buildAnnotationFigures(
  coordinates: Coord[],
  text: string,
  line: AnnotationStyle = {},
  textStyle: AnnotationStyle = {},
): OverlayFigure[] {
  const anchor = coordinates[0]
  if (!finite(anchor)) return []
  const startY = anchor.y - STEM_GAP
  const lineEndY = startY - STEM_LEN
  const arrowEndY = lineEndY - HEAD_H
  return [
    lineFigure({ x: anchor.x, y: startY }, { x: anchor.x, y: lineEndY }, line, true),
    {
      type: 'polygon',
      ignoreEvent: false,
      attrs: {
        coordinates: [
          { x: anchor.x, y: lineEndY },
          { x: anchor.x - HEAD_HALF_W, y: arrowEndY },
          { x: anchor.x + HEAD_HALF_W, y: arrowEndY },
        ],
      },
      styles: line.color !== undefined ? { color: line.color } : {},
    },
    {
      type: 'text',
      ignoreEvent: false,
      attrs: { x: anchor.x, y: arrowEndY, text, align: 'center', baseline: 'bottom' },
      styles: {
        ...(textStyle.color !== undefined ? { color: textStyle.color } : {}),
        ...(textStyle.size !== undefined ? { size: textStyle.size } : {}),
      },
    },
  ]
}

/** Register the interactive annotation template. Idempotent. */
export const registerAnnotationOverlay = registerOnce(() => {
  registerOverlay({
    name: 'simpleAnnotation',
    totalStep: 2, // single click
    ...DEFAULT_FIGURES,
    styles: { line: { style: 'dashed' } },
    createPointFigures: ({ coordinates, overlay }) => {
      const styles = (overlay as { styles?: { line?: AnnotationStyle; text?: AnnotationStyle } })
        ?.styles
      return buildAnnotationFigures(
        coordinates as Coord[],
        resolveAnnotationText(overlay),
        styles?.line ?? {},
        styles?.text ?? {},
      )
    },
  } as OverlayTemplateParam)
})
